/**
 * usePermissions Hook - Control centralizado de permisos y roles
 *
 * QUÉ HACE:
 * - Proporciona funciones para verificar permisos del usuario actual
 * - Determina acceso a módulos, vistas y acciones
 * - Soporta roles: dueño, administrador, cajero, vendedor, empleado
 *
 * QUÉ EXPORTA: usePermissions (named)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe modificar permisos (solo lectura)
 * - NO debe hacer llamadas a API
 *
 * REGLAS:
 * - TODA verificación de permisos DEBE usar este hook
 * - NO verificar roles manualmente en componentes
 * - El dueño SIEMPRE tiene acceso total
 *
 * Uso:
 *   const { canAccess, isOwner, isAdmin, userRole } = usePermissions();
 *   if (canAccess(["dueño", "administrador"])) { ... }
 *   if (canAccess(["all"], "caja")) { ... }
 */

import { useAuthStore } from "@/store/authStore";
import type { Permission } from "@/types";

export const usePermissions = () => {
  const { user } = useAuthStore();

  const userRole = user?.role?.toLowerCase() || "";
  const isOwner = userRole === "dueño";
  const isAdmin = userRole === "administrador";
  const isEmployee = userRole === "empleado";
  const isCashier = userRole === "cajero";

  /**
   * Verifica si el usuario tiene acceso basado en roles y/o permisos modulares
   * @param roles - Array de roles permitidos (e.g., ["dueño", "administrador"])
   * @param permissionKey - Clave de permiso modular opcional (e.g., "caja", "inventario")
   */
  const canAccess = (
    roles: string[],
    permissionKey?: keyof Permission,
    excludeRoles?: string[],
  ): boolean => {
    // Si el rol actual está explícitamente excluido, denegar acceso
    if (excludeRoles?.includes(userRole)) return false;

    // Dueño siempre tiene acceso total
    if (isOwner) return true;

    // Permisos modulares estrictos (si existen)
    if (user?.permissions && permissionKey) {
      const permission = user.permissions[permissionKey];
      return permission !== "none" && permission !== undefined;
    }

    // "all" permite acceso a todos
    if (roles.includes("all")) return true;

    // Verificación de rol específico
    return roles.includes(userRole);
  };

  /**
   * Verifica si el usuario puede editar en un módulo específico
   */
  const canEdit = (permissionKey: keyof Permission): boolean => {
    if (isOwner) return true;
    if (user?.permissions && permissionKey) {
      return user.permissions[permissionKey] === "edit";
    }
    return isAdmin;
  };

  /**
   * Verifica si el usuario puede ver un módulo específico
   */
  const canView = (permissionKey: keyof Permission): boolean => {
    if (isOwner || isAdmin) return true;
    if (user?.permissions && permissionKey) {
      const perm = user.permissions[permissionKey];
      return perm === "view" || perm === "edit";
    }
    return false;
  };

  return {
    user,
    userRole,
    isOwner,
    isAdmin,
    isEmployee,
    isCashier,
    canAccess,
    canEdit,
    canView,
  };
};
